import * as React from "react";

import { Option } from "../../../../types/Option";

import { cn } from "../../../../lib/utils";

import { Chip } from "../../Chip";

type ChipsInputProps<T> = {
	selectedOptions: Option<T>[];
	value: T[];
	onChange: (value: T[]) => void;
	isDisabled: boolean;
	placeholder: string;
	inputId: string;
	dataTestId?: string;
	renderValue?: (value: T, onDismiss?: () => void) => React.ReactNode;
	trailingContent?: React.ReactNode;
};

const ChipsInput = <T,>({
	selectedOptions,
	value,
	onChange,
	isDisabled,
	placeholder,
	inputId,
	dataTestId,
	renderValue,
	trailingContent,
}: ChipsInputProps<T>) => {
	const handleChipRemove = (optionToRemove: Option<T>) => {
		return (e?: React.MouseEvent) => {
			if (e) {
				e.preventDefault();
				e.stopPropagation();
			}
			if (!isDisabled) {
				const newValue = value.filter((val) => val !== optionToRemove.value);
				onChange(newValue);
			}
		};
	};

	const renderInput = (className?: string) => (
		<input
			className={cn(
				"w-full border-none bg-transparent text-paragraph-3 caret-transparent outline-hidden placeholder:text-neutral-5 focus:outline-hidden",
				isDisabled ? "cursor-not-allowed" : "cursor-pointer",
				className
			)}
			placeholder={selectedOptions.length === 0 ? placeholder : ""}
			data-testid={dataTestId}
			disabled={isDisabled}
			id={inputId}
			readOnly
		/>
	);

	return (
		<div className="flex h-full max-h-32 min-w-0 flex-1 flex-wrap items-center gap-0.5 overflow-x-hidden overflow-y-auto py-1 pr-0 pl-1">
			{selectedOptions.map((option, index) => {
				if (renderValue) {
					const customChip = renderValue(option.value, isDisabled ? undefined : handleChipRemove(option));

					return (
						<div
							key={`selected-${JSON.stringify(option.value)}-${index}`}
							className="flex max-w-full min-w-0 items-center [&>*]:!flex [&>*]:max-w-full"
						>
							{customChip}
						</div>
					);
				} else {
					return (
						<Chip
							key={`selected-${JSON.stringify(option.value)}-${index}`}
							label={option.label}
							color="neutral"
							size={20}
							onDismiss={isDisabled ? undefined : handleChipRemove(option)}
							dangerouslySetClassName="!flex max-w-full"
						/>
					);
				}
			})}

			{trailingContent ? (
				<div
					className={cn(
						"flex min-w-0 flex-1 items-center gap-1",
						isDisabled ? "cursor-not-allowed" : "cursor-pointer"
					)}
				>
					<div className="relative min-w-0">
						<span aria-hidden className="block truncate text-paragraph-3 text-neutral-5">
							{placeholder}
						</span>
						<span className="absolute inset-0 flex items-center">
							{renderInput("placeholder:text-transparent")}
						</span>
					</div>
					{trailingContent}
				</div>
			) : (
				<div className="min-w-0 flex-1">{renderInput()}</div>
			)}
		</div>
	);
};

ChipsInput.displayName = "ChipsInput";

export { ChipsInput };
